import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { FaCalendarAlt, FaDownload, FaEnvelope, FaTimes } from 'react-icons/fa';

import axios from '../../api/axios';
import BottomNav from '../../components/BottomNav';
import HeaderWithBack from '../../components/HeaderWithBack';
import SendEmail from '../../components/modal/SendEmail';
import Loading from '../../components/Loading';

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import '../../styles/styles.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const ReferenceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { referenceId } = useParams<{ referenceId: string }>();
  const storedData = sessionStorage.getItem('userData');
  const userData = storedData ? JSON.parse(storedData) : null;
  const token = userData ? userData.token : null;
  const email = userData ? userData.userData : null;

  const [referenceData, setReferenceData] = useState<any>(null);
  const [showPdf, setShowPdf] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`reference/show/${referenceId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        });

        const data = response.data.data;
        console.log(data, "reference data");
        setReferenceData(data);
      } catch (error) {
        console.error('Error fetching reference data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, referenceId]);

  // Check if reference is expired
  let isExpired = false;
  if (referenceData?.reference_expired_date) {
    const [day, month, year] = referenceData.reference_expired_date.split("-");
    const expiredDate = new Date(`${year}-${month}-${day}`);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiredDate.setHours(0, 0, 0, 0);

    isExpired = expiredDate < today;
  }

  const publicPdfUrl = referenceData?.pdf_url;
  const publicPdfUrlDownload = referenceData?.pdf_url_download;

  const handleDownloadPdf = async () => {
    try {
        setIsLoading(true);

        const response = await axios.post(
        `reference/download/${referenceId}`,
        {
            hospital_id: referenceData?.hospital_id,
        },
        {
            headers: {
            Authorization: `Bearer ${token}`,
            },
            responseType: 'blob', // PENTING
        }
        );

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `rujukan_${referenceData?.reference_number}.pdf`;
        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
    } catch (error) {
        console.error(error);
        alert('Gagal mengunduh PDF');
    } finally {
        setIsLoading(false);
    }
  };

  const handleSendEmail = () => { 
    setShowModal(true);
  };

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await axios.get(`brilife/reference/send-email/${referenceId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      alert('Berhasil mengirim rujukan ke email');
    } catch (error) {
      alert('Gagal mengirim rujukan');
    } finally {
      setIsLoading(false);
      setShowModal(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: any = {
      active: { color: 'bg-green-500', text: 'Aktif' },
      approved: { color: 'bg-green-500', text: 'Disetujui' },
      pending: { color: 'bg-yellow-500', text: 'Menunggu' },
      expired: { color: 'bg-red-500', text: 'Kadaluarsa' },
      rejected: { color: 'bg-red-500', text: 'Ditolak' },
    };

    const config = statusConfig[status?.toLowerCase()] || { color: 'bg-gray-500', text: status };
    return (
      <span className={`${config.color} text-white px-3 py-1 rounded-full text-xs font-semibold`}>
        {config.text}
      </span>
    );
  };

  if (isLoading && !referenceData) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <HeaderWithBack title="Detail Rujukan" />
        <div className="flex-1 flex items-center justify-center">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col pb-20">
      {/* Header */}
      <HeaderWithBack title="Detail Rujukan" />

      {/* Info Rujukan */}
      <div className="bg-teal-500 text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center">
          <FaCalendarAlt className="mr-2" />
          <span>Rujukan Anda</span>
        </div>
        {referenceData && getStatusBadge(referenceData.status)}
      </div>
      
      {/* Warning Expired */}
      {referenceData?.reference_expired_date && (
        isExpired ? (
          <div className="bg-red-500 text-white px-4 py-2 text-sm text-center">
            Rujukan Sudah Kadaluarsa <br /> <strong>{referenceData.reference_expired_date}</strong>
          </div>
        ) : (
          <div className="bg-yellow-100 text-yellow-800 px-4 py-2 text-sm text-center">
            Berlaku sampai <br /> <strong>{referenceData.reference_expired_date}</strong>
          </div>
        )
      )}

      {/* Detail Rujukan */}
      <div className="px-4 mt-4">
        <div className="bg-white border rounded-md shadow-sm p-4">
          <h2 className="font-semibold text-gray-800 mb-3">Informasi Rujukan</h2>
          
          {/* Nomor Rujukan */}
          <div className="mb-3 pb-3 border-b">
            <p className="text-xs text-gray-500 mb-1">Nomor Rujukan</p>
            <p className="font-semibold text-lg">{referenceData?.reference_number}</p>
          </div>

          {/* Provider Info */}
          <div className="mb-3 pb-3 border-b">
            <p className="text-xs text-gray-500 mb-1">Rumah Sakit Tujuan</p>
            <p className="font-semibold">{referenceData?.provider_name}</p>
            {referenceData?.provider_code && (
              <p className="text-sm text-gray-600">Kode: {referenceData.provider_code}</p>
            )}
          </div>

          {/* Poli Info */}
          <div className="mb-3 pb-3 border-b">
            <p className="text-xs text-gray-500 mb-1">Poliklinik</p>
            <p className="font-semibold">{referenceData?.poly_name || referenceData?.poly}</p>
            {referenceData?.poly_code && (
              <p className="text-sm text-gray-600">Kode Poli: {referenceData.poly_code}</p>
            )}
          </div>

          {/* Tipe & Tanggal */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Tipe Rujukan</p>
              <p className="font-semibold">{referenceData?.type}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Tanggal Rujukan</p>
              <p className="font-semibold">{referenceData?.reference_date}</p>
            </div>
          </div>

          {/* Catatan Dokter */}
          {referenceData?.doctor_note && (
            <div className="mt-4 bg-gray-50 p-3 rounded-md">
              <p className="text-xs text-gray-500 mb-1">Catatan Dokter</p>
              <p className="text-sm text-gray-700">{referenceData.doctor_note}</p>
            </div>
          )}

          {/* Notes */}
          {referenceData?.notes && (
            <div className="mt-3 bg-blue-50 p-3 rounded-md">
              <p className="text-xs text-gray-500 mb-1">Catatan</p>
              <p className="text-sm text-gray-700">{referenceData.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Tombol Lihat & Download PDF */}
      {!showPdf && !isExpired && (
        <div className="px-4 mt-4">
          <button
            onClick={handleDownloadPdf}
            type="button"
            className="w-full p-3 bg-teal-500 rounded-md text-white font-semibold flex items-center justify-center hover:bg-teal-600"
          >
            <FaDownload className="mr-2" /> Lihat & Download Rujukan
          </button>
        </div>
      )}

      {/* PDF Viewer */}
      {showPdf && publicPdfUrl && (
        <div className="px-4 mt-4">
          <div className="border rounded-md p-4 bg-white shadow-sm">
            <Document
              file={publicPdfUrl}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={<div className="text-center py-4"><Loading /></div>}
              error={<p className="text-center text-red-500 py-4">Gagal memuat PDF.</p>}
            >
              {Array.from(new Array(numPages), (el, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  width={window.innerWidth - 64}
                  className="mb-4"
                />
              ))}
            </Document>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                type="button"
                onClick={handleSendEmail}
                className="flex-1 p-2 bg-teal-500 rounded text-white text-sm flex items-center justify-center hover:bg-teal-600"
              >
                <FaEnvelope className="mr-2" /> Kirim Email
              </button>

              <button
                onClick={handleDownloadPdf}
                type="button"
                className="flex-1 p-2 bg-teal-600 rounded text-white text-sm flex items-center justify-center hover:bg-teal-700"
              >
                <FaDownload className="mr-2" /> Download
              </button>

              <button
                onClick={() => setShowPdf(false)}
                type="button"
                className="flex-1 p-2 bg-gray-500 rounded text-white text-sm flex items-center justify-center hover:bg-gray-600"
              >
                <FaTimes className="mr-2" /> Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <Loading />
        </div>
      )}

      {/* Send Email Modal */}
      <SendEmail
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirm}
        email={email}
      />

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default ReferenceDetail;